import { Component, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { getDocumentNumber, mongoId } from '../../../../@core/common/global';
import { CompilationModel } from '../../../../@models/erp/compilation';
import { SharedDataService } from '../../../../@core/service/shared-data.service';
import { QRCodeComponent } from 'angularx-qrcode';

export type BarcodeType = 'barcode' | 'qrcode';
@Component({
  selector: 'ngx-barcodes',
  templateUrl: './barcodes.component.html',
  styleUrls: ['./barcodes.component.scss'],
})
export class BarcodesComponent implements OnChanges {

  @Input() compilation: CompilationModel;
  @Input() disabled: boolean;
  @Input() print: boolean;
  @Input() type: BarcodeType = 'barcode';

  @Output() dataChange = new EventEmitter<{doc: any, items: any[]}>()

  @ViewChildren('svgref') svgRefs: QueryList<any>;

  private notBcCharsetCode39 = /[^A-Z0-9 *\-$%./+]/g; // code39
  notBcCharset = /[^\x00-\x7F]/g; // code128
  bcFormat = 'CODE128' as any;

  qrData = '';

  static sConfig = {
    doc: [
      { id: 'code', name: 'Document number', model: '', code: 'dcn', value: [] },
      { id: 'issueDate', name: 'Issue date', model: '', code: 'dcd', value: [] },
      { id: 'customerDocumentNumber', name: 'Customer document number', model: '', code: 'cdn', value: [] },
      { id: 'customerPoPosition', name: 'Customer document position', model: '', code: 'cdp', value: [] },
    ],
    item: [
      { id: 'productName', name: 'Line 1', model: '', code: 'lpn', value: [] },
      { id: 'attributeRemark', name: 'Line 2', model: '', code: 'lar', value: [] },
      { id: 'attributeCode', name: 'Customer reference', model: '', code: 'lac', value: [] },
      { id: 'quantity', name: 'Quantity', model: '', code: 'liq', value: [] },
    ],
  }
  config = BarcodesComponent.sConfig;

  model = {
    doc: [],
    items: [[]],
  }
  
  constructor(private data: SharedDataService) { }

  async ngOnChanges(changes: SimpleChanges) {
    if (!changes.compilation) return;
    const isAll = this.compilation.barcodes && this.compilation.barcodes[0] === 'all';

    await this.data.loadBusinesses();
    const vem = this.data.businesses.find(f => f.name === 'VEM DE DRS');
    if (mongoId(vem) === mongoId(this.compilation.recipient)) {
      // use CODE39 for this customer
      this.notBcCharset = this.notBcCharsetCode39;
      this.bcFormat = 'CODE39';
    }

    if (!this.compilation.barcodes || isAll) {
      this.compilation.barcodes = [];
      for (const docline of this.config.doc) if (docline.code !== 'cdp') this.compilation.barcodes.push(docline.code);
      this.compilation.lineItems.forEach((el, idx) => {
        for (const itemline of this.config.item) {
          if (!['lpn', 'lar'].includes(itemline.code)) this.compilation.barcodes.push(itemline.code + '-' + idx);
        }
      })
    }
    for (const item of this.config.doc) {
      this.model.doc[item.code] = isAll;
    }
    this.compilation.lineItems.forEach((el, idx) => {
      for (const item of this.config.item) {
        if (!this.model.items[idx]) this.model.items[idx] = [];
        this.model.items[idx][item.code] = isAll;
      }
    })

    this.getModel();

    for (const field of this.config.doc) {
      if (!this.print || (this.model.doc && this.model.doc[field.code])) field.value[0] = this.clean(field);
    }
    this.compilation.lineItems.forEach((el, idx) => {
      for (const field of this.config.item) {
        if (!this.print || (this.model.items[idx] && this.model.items[idx][field.code])) {
          field.value[idx] = this.clean(field, idx);
        }
      }
    })

    this.setQrData(this.model);
  }

  setQrData(model: any) {
    // console.log('set qr data', this.type, model);
    this.qrData = 
    this.config.doc.map(field => `${field.name}: ${field.value[0]}`).join('\n') + '\n'
    + this.compilation.lineItems.flatMap((m, idx) => 
      this.config.item.map(field => 
        model.items?.[idx]?.[field.code] && `Pos [${idx+1}] ${field.name}: ${field.value[idx]}`)
      .filter(notNull => notNull)
    ).join('\n')

    console.log('set qr data', this.qrData);
  }

  getModel() {
    for (const bc of this.compilation.barcodes) {
      const [code, idx] = bc.split('-');
      if (idx) {
        if (this.model && this.model.items && this.model.items[idx]) this.model.items[idx][code] = true;
      } else {
        if (this.model && this.model.doc) this.model.doc[code] = true;
      }
    }
  }

  setModel() {
    const licodes = [];
    let length = 0;
    for (const code of Object.keys(this.model.doc)) {
      if (this.model.doc[code]) licodes.push(code);
      length++;
    }
    this.compilation.lineItems.forEach((el, idx) => {
      for (const code of Object.keys(this.model.items[idx])) {
        if (this.model.items[idx][code]) licodes.push(`${code}-${idx}`);
        length++;
      }
    })
    this.compilation.barcodes = licodes.length === length ? ['all'] : licodes;

    this.dataChange.emit(this.model);
  }

  clean(field: any, idx = -1) {
    let val = idx > -1 ? this.compilation.lineItems[idx][field.id] : this.compilation[field.id];
    if (typeof val === 'number') val = ''+val;
    // if (val?.length > 70) val = val.slice(0, 67) + '...';
    switch (field.id) {
      case 'code':
        val = getDocumentNumber(this.compilation, null, false);
        break;
      case 'productName':
        val = val && val.replace(/;.*/, '');
        break;
      case 'issueDate':
        const d = new Date(val);
        val = d && d.toISOString().replace(/T.*/, '');
        break;
      default:
        val = (typeof val === 'string' && val.replace(this.notBcCharset, ' ')) || 'N/A'; // code128/39
    }
    if (!val || !val.length) val = 'N/A';
    return val;
  }

}
